'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import { BATTLESHIP, OLAF, THUNDERDOME } from 'HubStyleTokens/colors';
import { getSide, getEdge } from '../utils/Placement';
import { USE_CLASSES } from '../utils/Use';
import { OpenPopupContext } from '../../context/internal/OpenPopupContext';
import { useSubscribe } from '../../hooks/pubSub';
import memoWithDisplayName from '../../utils/memoWithDisplayName';
import { toPx } from '../../utils/Styles';
const mediumArrowSize = 16;
const smallArrowSize = 12;

const isShepherdArrowWhite = (placement, headerHeight, inset, arrowWidth) => {
  // If we have an inset on the horizontal sides, we can't trust the default placement as truth
  const sidePlacement = getSide(placement);

  if (inset && (sidePlacement === 'left' || sidePlacement === 'right')) {
    if (headerHeight) {
      return inset + arrowWidth / 2 > headerHeight;
    } // if no valid headerHeight, ignore and default to base calc;

  }

  return sidePlacement !== 'bottom' && getEdge(placement) !== 'bottom';
};

const adjustInsetForShepherdArrows = (placement, headerHeight, inset, arrowWidth) => {
  const fixedOffset = Math.sqrt(arrowWidth);
  const sidePlacement = getSide(placement);

  if (inset && (sidePlacement === 'left' || sidePlacement === 'right')) {
    if (headerHeight) {
      /**
       * If header height is known, avoid placements which cause the arrow to span both the purple and white background
       */
      const hypotenuse = Math.sqrt(2 * (arrowWidth * arrowWidth));
      const arrowMidPointOffset = inset + hypotenuse / 2; // Arrow within the header (purple area), but partially overlaps the header-body border

      if (arrowMidPointOffset <= headerHeight && arrowMidPointOffset > headerHeight - hypotenuse / 2 + fixedOffset) {
        return headerHeight - hypotenuse + 4;
      } // Arrow below the header (white area), but partially overlaps the header-body border


      if (arrowMidPointOffset > headerHeight && arrowMidPointOffset <= headerHeight + hypotenuse / 2 + fixedOffset) {
        return headerHeight + 4;
      }
    } // if no valid headerHeight, ignore and treat like default.

  } // Otherwise return the original inset, no adjustments made.


  return inset;
};

const getBackgroundColor = ({
  color,
  headerHeight,
  inset,
  placement,
  popoverUse,
  width
}) => {
  if (color) {
    return color;
  }

  if (popoverUse === 'shepherd') {
    return isShepherdArrowWhite(placement, headerHeight, inset, width) ? OLAF : THUNDERDOME;
  }

  return 'inherit';
};

const getSideStyles = ({
  placement,
  width
}) => {
  switch (getSide(placement)) {
    case 'top':
      // Arrow points down
      return css(["transform:rotate(45deg);top:calc(100% - ", "px);"], width / 2);

    case 'right':
      // Arrow points left
      return css(["transform:rotate(135deg);left:-", "px;"], width / 2);

    case 'bottom':
      // Arrow points up
      return css(["transform:rotate(-135deg);top:-", "px;"], width / 2);

    case 'left':
      // Arrow points right
      return css(["transform:rotate(-45deg);left:calc(100% - ", "px);"], width / 2);

    default:
      return '';
  }
};

const getEdgeStyles = ({
  inset,
  placement
}) => {
  switch (getEdge(placement)) {
    case 'top':
    case 'bottom':
    case 'middle':
      return css(["top:", "px;"], inset);

    case 'left':
    case 'right':
    case 'center':
      return css(["left:", "px;"], inset);

    default:
      return '';
  }
};

const getBorderUseStyles = ({
  headerHeight,
  inset,
  placement,
  popoverUse,
  width
}) => {
  if (popoverUse === 'default' || popoverUse === 'longform' || popoverUse === 'shepherd' && isShepherdArrowWhite(placement, headerHeight, inset, width)) {
    return `1px solid ${BATTLESHIP}`;
  }

  return null;
};

const Arrow = styled.div.withConfig({
  displayName: "UIPopoverArrow__Arrow",
  componentId: "sc-1iv0vgp-0"
})(["position:absolute;pointer-events:none;border:", ";clip-path:polygon(100% 100%,0 100%,100% 0);border-top-left-radius:100%;border-top-color:transparent !important;border-left-color:transparent !important;border-bottom-right-radius:", ";width:", ";height:", ";background-color:", ";", ";", ";.tether-pinned &{display:none;}"], getBorderUseStyles, ({
  popoverUse
}) => popoverUse.includes('tooltip') ? '3px' : null, ({
  width
}) => toPx(width), ({
  width
}) => toPx(width), getBackgroundColor, getSideStyles, getEdgeStyles);
const UIPopoverArrow = memoWithDisplayName('UIPopoverArrow', (_ref) => {
  let {
    className,
    width
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "width"]);

  const {
    positionChannel
  } = useContext(OpenPopupContext);
  const [positionConfig, setPositionConfig] = useState();
  useSubscribe(positionChannel, positionUpdate => {
    setPositionConfig(positionUpdate);
  });
  if (!positionConfig) return null;
  const {
    arrowInset,
    pinning,
    placement
  } = positionConfig;
  if (!!pinning || width === 0) return null;
  let computedArrowInset = arrowInset;
  const {
    headerHeight,
    popoverUse
  } = rest;

  if (popoverUse === 'shepherd') {
    computedArrowInset = adjustInsetForShepherdArrows(placement, headerHeight, arrowInset, width);
  }

  return /*#__PURE__*/_jsx(Arrow, Object.assign({}, rest, {
    className: classNames('uiPopoverArrow private-popover__arrow', className),
    "data-popover-arrow": true,
    inset: computedArrowInset,
    placement: placement,
    width: width
  }));
});

if (process.env.NODE_ENV !== 'production') {
  UIPopoverArrow.propTypes = {
    color: PropTypes.string,
    headerHeight: PropTypes.number,
    popoverUse: PropTypes.oneOf(Object.keys(USE_CLASSES)),
    width: PropTypes.number
  };
}

UIPopoverArrow.defaultProps = {
  width: 20
};
UIPopoverArrow.ARROW_SIZES = {
  none: {
    distance: 4,
    width: 0
  },
  medium: {
    distance: mediumArrowSize,
    width: 20
  },
  small: {
    distance: smallArrowSize,
    width: 16
  }
};
export default UIPopoverArrow;